// Cost & Sales Category Mapping Storage — Task 24
//
// Company-wide mapping from Cost/Sales Budget Category Type → Nominal Codes.
// Allows Finance Admins to assign one or more nominal/GL codes to each category.
//
// Phase 1: localStorage-backed UI shell with mock nominal codes.
//          Backend endpoints do not exist yet — this is a ready-to-wire skeleton.
//
// Phase 2: Replace read/write calls with:
//   GET  /api/finance/nominal-codes             → load available nominal codes
//   GET  /api/settings/finance/category-mappings → load saved mappings
//   POST /api/settings/finance/category-mappings → save a new mapping entry
//   PUT  /api/settings/finance/category-mappings/{id} → update a mapping entry
//   DELETE /api/settings/finance/category-mappings/{id} → remove a mapping entry

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export type CostCategoryType =
  | 'labour'
  | 'expenses'
  | 'subcontractors'
  | 'materials'
  | 'equipment'
  | 'overhead'
  | 'contingency';

export type SalesCategoryType =
  | 'labour'
  | 'expenses'
  | 'subcontractors'
  | 'materials'
  | 'equipment';

export interface NominalCode {
  /** e.g. "4000" */
  nominalCode: string;
  /** e.g. "Labour - Permanent Staff" */
  nominalName: string;
  /** e.g. "Cost" | "Sales" */
  nominalType: string;
  /** Debit nominal code reference */
  debit: string;
  /** Name of the debit account */
  debitAccountName: string;
  /** Credit nominal code reference */
  credit: string;
  /** Name of the credit account */
  creditAccountName: string;
  /** VAT code, e.g. "T1" */
  vat: string;
  /** Name of the VAT account */
  vatAccountName: string;
  /** Sort key for ordering */
  sortKey: string;
}

/** One row in the mapping table — one nominal code assigned to one category type */
export interface CategoryNominalMapping {
  id: string;
  categoryType: CostCategoryType | SalesCategoryType;
  nominalCode: string;
  nominalName: string;
  /** Optional secondary filter, e.g. 'Timesheet' — null means "all transaction types" */
  transactionType: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Cost category display metadata
// ─────────────────────────────────────────────────────────────────────────────

export const COST_CATEGORY_LABELS: Record<CostCategoryType, string> = {
  labour: 'Labour',
  expenses: 'Expenses',
  subcontractors: 'Subcontractors',
  materials: 'Materials',
  equipment: 'Equipment',
  overhead: 'Overhead',
  contingency: 'Contingency',
};

export const COST_CATEGORY_DESCRIPTIONS: Record<CostCategoryType, string> = {
  labour: 'Internal staff timesheets and payroll costs',
  expenses: 'Reimbursable expenses claimed by team members',
  subcontractors: 'External contractors and freelancers',
  materials: 'Physical goods, consumables and raw materials',
  equipment: 'Hired or purchased equipment and plant',
  overhead: 'Allocated overhead and indirect costs',
  contingency: 'Contingency reserve for risk and scope changes',
};

/** Ordered list — matches the cost table row order on the settings page */
export const COST_CATEGORY_ORDER: CostCategoryType[] = [
  'labour',
  'expenses',
  'subcontractors',
  'materials',
  'equipment',
  'overhead',
  'contingency',
];

// ─────────────────────────────────────────────────────────────────────────────
// Sales category display metadata
// ─────────────────────────────────────────────────────────────────────────────

export const SALES_CATEGORY_LABELS: Record<SalesCategoryType, string> = {
  labour: 'Labour',
  expenses: 'Expenses',
  subcontractors: 'Subcontractors',
  materials: 'Materials',
  equipment: 'Equipment',
};

export const SALES_CATEGORY_DESCRIPTIONS: Record<SalesCategoryType, string> = {
  labour: 'Revenue recognised from time charged to the client (T&M billing)',
  expenses: 'Recharged expenses billed to the client at cost or marked up',
  subcontractors: 'Subcontractor costs recharged to the client',
  materials: 'Materials supplied and billed to the client',
  equipment: 'Equipment hire or purchase recharged to the client',
};

/** Ordered list — matches the sales table row order on the settings page */
export const SALES_CATEGORY_ORDER: SalesCategoryType[] = [
  'labour',
  'expenses',
  'subcontractors',
  'materials',
  'equipment',
];

// ─────────────────────────────────────────────────────────────────────────────
// Mock COST nominal codes (Phase 1 placeholder — replaced by GET /api/finance/nominal-codes)
// ─────────────────────────────────────────────────────────────────────────────

export const MOCK_NOMINAL_CODES: NominalCode[] = [
  // Labour
  { nominalCode: '4000', nominalName: 'Labour - Permanent Staff',         nominalType: 'Cost', debit: '4000', debitAccountName: 'Wages & Salaries',         credit: '2100', creditAccountName: 'PAYE/NIC Creditor',         vat: 'T0', vatAccountName: 'Zero Rated',    sortKey: '010' },
  { nominalCode: '4010', nominalName: 'Labour - Contractors',             nominalType: 'Cost', debit: '4010', debitAccountName: 'Contractor Costs',          credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '020' },
  { nominalCode: '4020', nominalName: 'Labour - Overtime',                nominalType: 'Cost', debit: '4020', debitAccountName: 'Overtime Costs',            credit: '2100', creditAccountName: 'PAYE/NIC Creditor',         vat: 'T0', vatAccountName: 'Zero Rated',    sortKey: '030' },
  // Expenses
  { nominalCode: '4100', nominalName: 'Staff Expenses - Travel',          nominalType: 'Cost', debit: '4100', debitAccountName: 'Travel & Transport',        credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '040' },
  { nominalCode: '4110', nominalName: 'Staff Expenses - Accommodation',   nominalType: 'Cost', debit: '4110', debitAccountName: 'Accommodation Costs',       credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '050' },
  { nominalCode: '4120', nominalName: 'Staff Expenses - Subsistence',     nominalType: 'Cost', debit: '4120', debitAccountName: 'Subsistence',               credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '060' },
  { nominalCode: '4130', nominalName: 'Staff Expenses - Sundry',          nominalType: 'Cost', debit: '4130', debitAccountName: 'Sundry Expenses',           credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '070' },
  // Subcontractors
  { nominalCode: '5000', nominalName: 'Subcontractors - Professional',    nominalType: 'Cost', debit: '5000', debitAccountName: 'Professional Services',     credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '080' },
  { nominalCode: '5010', nominalName: 'Subcontractors - Technical',       nominalType: 'Cost', debit: '5010', debitAccountName: 'Technical Subcontractors',  credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '090' },
  { nominalCode: '5020', nominalName: 'Subcontractors - Specialist',      nominalType: 'Cost', debit: '5020', debitAccountName: 'Specialist Subcontractors', credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '100' },
  // Materials
  { nominalCode: '5200', nominalName: 'Materials - Raw',                  nominalType: 'Cost', debit: '5200', debitAccountName: 'Raw Materials',             credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '110' },
  { nominalCode: '5210', nominalName: 'Materials - Consumables',          nominalType: 'Cost', debit: '5210', debitAccountName: 'Consumables',               credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '120' },
  { nominalCode: '5220', nominalName: 'Materials - Purchased Components', nominalType: 'Cost', debit: '5220', debitAccountName: 'Purchased Components',      credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '130' },
  // Equipment
  { nominalCode: '5300', nominalName: 'Equipment Hire',                   nominalType: 'Cost', debit: '5300', debitAccountName: 'Plant Hire',                credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '140' },
  { nominalCode: '5310', nominalName: 'Equipment Purchase',               nominalType: 'Cost', debit: '5310', debitAccountName: 'Capital Equipment',         credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '150' },
  { nominalCode: '5320', nominalName: 'Plant & Machinery',                nominalType: 'Cost', debit: '5320', debitAccountName: 'Plant & Machinery',         credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '160' },
  // Overhead
  { nominalCode: '6000', nominalName: 'Overhead - Allocated',             nominalType: 'Cost', debit: '6000', debitAccountName: 'Overhead Allocation',       credit: '6900', creditAccountName: 'Overhead Absorption',        vat: 'T0', vatAccountName: 'Zero Rated',    sortKey: '170' },
  { nominalCode: '6010', nominalName: 'Overhead - IT & Software',         nominalType: 'Cost', debit: '6010', debitAccountName: 'IT Costs',                  credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '180' },
  { nominalCode: '6020', nominalName: 'Overhead - Facilities',            nominalType: 'Cost', debit: '6020', debitAccountName: 'Facilities Costs',          credit: '2200', creditAccountName: 'Accounts Payable',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '190' },
  // Contingency
  { nominalCode: '9900', nominalName: 'Contingency Reserve',              nominalType: 'Cost', debit: '9900', debitAccountName: 'Contingency Provision',     credit: '9950', creditAccountName: 'Contingency Reserve Fund',  vat: 'T0', vatAccountName: 'Zero Rated',    sortKey: '200' },
  { nominalCode: '9910', nominalName: 'Risk Provision',                   nominalType: 'Cost', debit: '9910', debitAccountName: 'Risk Allowance',            credit: '9950', creditAccountName: 'Contingency Reserve Fund',  vat: 'T0', vatAccountName: 'Zero Rated',    sortKey: '210' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Mock SALES nominal codes (Phase 1 placeholder — replaced by GET /api/finance/nominal-codes)
// ─────────────────────────────────────────────────────────────────────────────

export const MOCK_SALES_NOMINAL_CODES: NominalCode[] = [
  // Labour / Time revenue
  { nominalCode: '3000', nominalName: 'Professional Services Revenue',    nominalType: 'Sales', debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3000', creditAccountName: 'Professional Services',      vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '010' },
  { nominalCode: '3010', nominalName: 'Consultancy Revenue',              nominalType: 'Sales', debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3010', creditAccountName: 'Consultancy Income',         vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '020' },
  { nominalCode: '3020', nominalName: 'Technical Services Revenue',       nominalType: 'Sales', debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3020', creditAccountName: 'Technical Services Income',  vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '030' },
  { nominalCode: '3030', nominalName: 'Managed Services Revenue',         nominalType: 'Sales', debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3030', creditAccountName: 'Managed Services Income',    vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '040' },
  // Expenses recharged
  { nominalCode: '3100', nominalName: 'Recharged Expenses - Travel',      nominalType: 'Sales', debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3100', creditAccountName: 'Recharged Travel',           vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '050' },
  { nominalCode: '3110', nominalName: 'Recharged Expenses - Accommodation',nominalType: 'Sales',debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3110', creditAccountName: 'Recharged Accommodation',    vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '060' },
  { nominalCode: '3120', nominalName: 'Recharged Expenses - Sundry',      nominalType: 'Sales', debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3120', creditAccountName: 'Recharged Sundry Expenses',  vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '070' },
  // Subcontractors recharged
  { nominalCode: '3200', nominalName: 'Recharged Subcontractors',         nominalType: 'Sales', debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3200', creditAccountName: 'Subcontractor Recharges',    vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '080' },
  { nominalCode: '3210', nominalName: 'Recharged Third-Party Services',   nominalType: 'Sales', debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3210', creditAccountName: 'Third-Party Recharges',      vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '090' },
  // Materials recharged
  { nominalCode: '3300', nominalName: 'Recharged Materials',              nominalType: 'Sales', debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3300', creditAccountName: 'Materials Revenue',          vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '100' },
  { nominalCode: '3310', nominalName: 'Recharged Components',             nominalType: 'Sales', debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3310', creditAccountName: 'Components Revenue',         vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '110' },
  { nominalCode: '3320', nominalName: 'Recharged Consumables',            nominalType: 'Sales', debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3320', creditAccountName: 'Consumables Revenue',        vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '120' },
  // Equipment recharged
  { nominalCode: '3400', nominalName: 'Recharged Equipment Hire',         nominalType: 'Sales', debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3400', creditAccountName: 'Equipment Hire Revenue',     vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '130' },
  { nominalCode: '3410', nominalName: 'Recharged Plant & Machinery',      nominalType: 'Sales', debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3410', creditAccountName: 'Plant Hire Revenue',         vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '140' },
  // Other revenue
  { nominalCode: '3500', nominalName: 'Licence & Subscription Revenue',   nominalType: 'Sales', debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3500', creditAccountName: 'Licence Revenue',            vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '150' },
  { nominalCode: '3510', nominalName: 'Support & Maintenance Revenue',    nominalType: 'Sales', debit: '1100', debitAccountName: 'Trade Debtors',             credit: '3510', creditAccountName: 'Support Revenue',            vat: 'T1', vatAccountName: 'Standard Rate', sortKey: '160' },
];

// ─────────────────────────────────────────────────────────────────────────────
// Default mock COST mappings — pre-populated so the page is never empty
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_MOCK_MAPPINGS: CategoryNominalMapping[] = [
  // Labour → 3 codes
  { id: 'seed-1',  categoryType: 'labour',         nominalCode: '4000', nominalName: 'Labour - Permanent Staff',         transactionType: null },
  { id: 'seed-2',  categoryType: 'labour',         nominalCode: '4010', nominalName: 'Labour - Contractors',             transactionType: null },
  { id: 'seed-3',  categoryType: 'labour',         nominalCode: '4020', nominalName: 'Labour - Overtime',                transactionType: null },
  // Expenses → 4 codes
  { id: 'seed-4',  categoryType: 'expenses',       nominalCode: '4100', nominalName: 'Staff Expenses - Travel',          transactionType: null },
  { id: 'seed-5',  categoryType: 'expenses',       nominalCode: '4110', nominalName: 'Staff Expenses - Accommodation',   transactionType: null },
  { id: 'seed-6',  categoryType: 'expenses',       nominalCode: '4120', nominalName: 'Staff Expenses - Subsistence',     transactionType: null },
  { id: 'seed-7',  categoryType: 'expenses',       nominalCode: '4130', nominalName: 'Staff Expenses - Sundry',          transactionType: null },
  // Subcontractors → 3 codes
  { id: 'seed-8',  categoryType: 'subcontractors', nominalCode: '5000', nominalName: 'Subcontractors - Professional',    transactionType: null },
  { id: 'seed-9',  categoryType: 'subcontractors', nominalCode: '5010', nominalName: 'Subcontractors - Technical',       transactionType: null },
  { id: 'seed-10', categoryType: 'subcontractors', nominalCode: '5020', nominalName: 'Subcontractors - Specialist',      transactionType: null },
  // Materials → 3 codes
  { id: 'seed-11', categoryType: 'materials',      nominalCode: '5200', nominalName: 'Materials - Raw',                  transactionType: null },
  { id: 'seed-12', categoryType: 'materials',      nominalCode: '5210', nominalName: 'Materials - Consumables',          transactionType: null },
  { id: 'seed-13', categoryType: 'materials',      nominalCode: '5220', nominalName: 'Materials - Purchased Components', transactionType: null },
  // Equipment → 3 codes
  { id: 'seed-14', categoryType: 'equipment',      nominalCode: '5300', nominalName: 'Equipment Hire',                   transactionType: null },
  { id: 'seed-15', categoryType: 'equipment',      nominalCode: '5310', nominalName: 'Equipment Purchase',               transactionType: null },
  { id: 'seed-16', categoryType: 'equipment',      nominalCode: '5320', nominalName: 'Plant & Machinery',                transactionType: null },
  // Overhead → 3 codes
  { id: 'seed-17', categoryType: 'overhead',       nominalCode: '6000', nominalName: 'Overhead - Allocated',             transactionType: null },
  { id: 'seed-18', categoryType: 'overhead',       nominalCode: '6010', nominalName: 'Overhead - IT & Software',         transactionType: null },
  { id: 'seed-19', categoryType: 'overhead',       nominalCode: '6020', nominalName: 'Overhead - Facilities',            transactionType: null },
  // Contingency → 2 codes
  { id: 'seed-20', categoryType: 'contingency',    nominalCode: '9900', nominalName: 'Contingency Reserve',              transactionType: null },
  { id: 'seed-21', categoryType: 'contingency',    nominalCode: '9910', nominalName: 'Risk Provision',                   transactionType: null },
];

// ─────────────────────────────────────────────────────────────────────────────
// Default mock SALES mappings — pre-populated so the page is never empty
// ─────────────────────────────────────────────────────────────────────────────

export const DEFAULT_MOCK_SALES_MAPPINGS: CategoryNominalMapping[] = [
  // Labour → 2 codes
  { id: 'sales-seed-1',  categoryType: 'labour',         nominalCode: '3000', nominalName: 'Professional Services Revenue',  transactionType: null },
  { id: 'sales-seed-2',  categoryType: 'labour',         nominalCode: '3010', nominalName: 'Consultancy Revenue',             transactionType: null },
  // Expenses → 2 codes
  { id: 'sales-seed-3',  categoryType: 'expenses',       nominalCode: '3100', nominalName: 'Recharged Expenses - Travel',     transactionType: null },
  { id: 'sales-seed-4',  categoryType: 'expenses',       nominalCode: '3120', nominalName: 'Recharged Expenses - Sundry',     transactionType: null },
  // Subcontractors → 1 code
  { id: 'sales-seed-5',  categoryType: 'subcontractors', nominalCode: '3200', nominalName: 'Recharged Subcontractors',        transactionType: null },
  // Materials → 2 codes
  { id: 'sales-seed-6',  categoryType: 'materials',      nominalCode: '3300', nominalName: 'Recharged Materials',             transactionType: null },
  { id: 'sales-seed-7',  categoryType: 'materials',      nominalCode: '3310', nominalName: 'Recharged Components',            transactionType: null },
  // Equipment → 1 code
  { id: 'sales-seed-8',  categoryType: 'equipment',      nominalCode: '3400', nominalName: 'Recharged Equipment Hire',        transactionType: null },
];

// ─────────────────────────────────────────────────────────────────────────────
// Per-category-instance storage (used by CategoryFormDialog)
// Keyed by GlobalCategory.id so each individual category row has its own codes.
// ─────────────────────────────────────────────────────────────────────────────

export interface CategoryInstanceMapping {
  costCodes: string[];
  salesCodes: string[];
}

const INSTANCE_STORAGE_KEY = 'category-instance-nominal-mappings';

type InstanceMappingStore = Record<string, CategoryInstanceMapping>;

function loadInstanceStore(): InstanceMappingStore {
  try {
    const stored = localStorage.getItem(INSTANCE_STORAGE_KEY);
    if (stored) return JSON.parse(stored) as InstanceMappingStore;
  } catch {
    // ignore
  }
  return {};
}

function saveInstanceStore(store: InstanceMappingStore): void {
  try {
    localStorage.setItem(INSTANCE_STORAGE_KEY, JSON.stringify(store));
  } catch (error) {
    console.error('Error saving category instance mappings:', error);
  }
}

/**
 * Get the cost + sales nominal codes assigned to a specific category instance.
 */
export function getCategoryInstanceMapping(categoryId: string): CategoryInstanceMapping {
  const store = loadInstanceStore();
  return store[categoryId] ?? { costCodes: [], salesCodes: [] };
}

/**
 * Persist the cost + sales nominal codes for a specific category instance.
 */
export function saveCategoryInstanceMapping(
  categoryId: string,
  mapping: CategoryInstanceMapping,
): void {
  const store = loadInstanceStore();
  store[categoryId] = mapping;
  saveInstanceStore(store);
}

/**
 * Remove the mapping for a deleted category instance.
 */
export function deleteCategoryInstanceMapping(categoryId: string): void {
  const store = loadInstanceStore();
  delete store[categoryId];
  saveInstanceStore(store);
}

// ─────────────────────────────────────────────────────────────────────────────
// Storage — Cost mappings
// ─────────────────────────────────────────────────────────────────────────────

const COST_STORAGE_KEY = 'cost-category-mappings';

/**
 * Load cost category → nominal code mappings from localStorage.
 * Falls back to DEFAULT_MOCK_MAPPINGS when nothing has been saved yet.
 */
export function getCategoryMappings(): CategoryNominalMapping[] {
  try {
    const stored = localStorage.getItem(COST_STORAGE_KEY);
    if (stored) return JSON.parse(stored) as CategoryNominalMapping[];
  } catch (error) {
    console.error('Error loading cost category mappings:', error);
  }
  return DEFAULT_MOCK_MAPPINGS;
}

/**
 * Persist the full cost mapping list to localStorage.
 */
export function saveCategoryMappings(mappings: CategoryNominalMapping[]): void {
  try {
    localStorage.setItem(COST_STORAGE_KEY, JSON.stringify(mappings));
  } catch (error) {
    console.error('Error saving cost category mappings:', error);
    throw error;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Storage — Sales mappings
// ─────────────────────────────────────────────────────────────────────────────

const SALES_STORAGE_KEY = 'sales-category-mappings';

/**
 * Load sales category → nominal code mappings from localStorage.
 * Falls back to DEFAULT_MOCK_SALES_MAPPINGS when nothing has been saved yet.
 */
export function getSalesCategoryMappings(): CategoryNominalMapping[] {
  try {
    const stored = localStorage.getItem(SALES_STORAGE_KEY);
    if (stored) return JSON.parse(stored) as CategoryNominalMapping[];
  } catch (error) {
    console.error('Error loading sales category mappings:', error);
  }
  return DEFAULT_MOCK_SALES_MAPPINGS;
}

/**
 * Persist the full sales mapping list to localStorage.
 */
export function saveSalesCategoryMappings(mappings: CategoryNominalMapping[]): void {
  try {
    localStorage.setItem(SALES_STORAGE_KEY, JSON.stringify(mappings));
  } catch (error) {
    console.error('Error saving sales category mappings:', error);
    throw error;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Derive the set of nominal codes currently assigned to each cost category type.
 * Returns a map: categoryType → string[] of nominalCodes.
 */
export function getMappingsByCategoryType(
  mappings: CategoryNominalMapping[]
): Record<CostCategoryType, string[]> {
  const result = {} as Record<CostCategoryType, string[]>;
  for (const cat of COST_CATEGORY_ORDER) {
    result[cat] = mappings.filter((m) => m.categoryType === cat).map((m) => m.nominalCode);
  }
  return result;
}

/**
 * Derive the set of nominal codes currently assigned to each sales category type.
 * Returns a map: categoryType → string[] of nominalCodes.
 */
export function getSalesMappingsByCategoryType(
  mappings: CategoryNominalMapping[]
): Record<SalesCategoryType, string[]> {
  const result = {} as Record<SalesCategoryType, string[]>;
  for (const cat of SALES_CATEGORY_ORDER) {
    result[cat] = mappings.filter((m) => m.categoryType === cat).map((m) => m.nominalCode);
  }
  return result;
}

/**
 * Returns any nominal codes that are assigned to more than one category (double-counting risk).
 */
export function findDuplicateMappings(
  mappings: CategoryNominalMapping[]
): Record<string, (CostCategoryType | SalesCategoryType)[]> {
  const codeToCategories: Record<string, (CostCategoryType | SalesCategoryType)[]> = {};
  for (const m of mappings) {
    if (!codeToCategories[m.nominalCode]) codeToCategories[m.nominalCode] = [];
    codeToCategories[m.nominalCode].push(m.categoryType);
  }
  return Object.fromEntries(
    Object.entries(codeToCategories).filter(([, cats]) => cats.length > 1)
  );
}
